/* The check-tuning offer.
 *
 * The third feedback loop, and the only one that reaches across documents. When
 * the same check has been set aside three times for the same reason, that is a
 * fact about this reviewer's work rather than about this plan, and it is worth
 * saying out loud once.
 *
 * Three properties make it safe to say:
 *
 *   It is an offer, never an action. Nothing is muted until the reviewer
 *   presses the button, and "Keep showing it" is a real answer that is recorded
 *   so the same three decisions are never raised twice.
 *
 *   Muting hides nothing. A muted check still runs, and its findings sit in a
 *   collapsed group at the bottom of the list. A federal reviewer must be able
 *   to see everything the tool found, whatever they have told it about their
 *   own preferences.
 *
 *   It is reversible from somewhere they can find: /checks lists every muted
 *   check and turns it back on.
 *
 * The sentence is specified in docs/redesign/BRIEF.md step 14 and is rendered
 * verbatim. The clause after "because" comes from DISMISS_REASONS, which is why
 * that table carries a `because` form alongside its label. */

import { Button, Label } from '../primitives';
import { dismissReasonBecause } from '../../data/review';
import type { MuteCandidate } from '../../data/review';
import { plainTitleOf } from '../../data/taxonomy';
import type { FailureModeId } from '../../types';
import './CheckTuning.css';

export interface CheckTuningProps {
  candidate: MuteCandidate;
  /** Quiet this check by default. Per browser, and always reversible. */
  onMute(): void;
  /** Leave it alone, and do not ask again about these same decisions. */
  onKeep(): void;
}

/**
 * The sentence, as one string, so it can be asserted rather than read out of
 * markup. The count is always at least MUTE_THRESHOLD, so it is never "1 time".
 */
export function tuningSentence(candidate: MuteCandidate): string {
  const title = plainTitleOf(candidate.failureModeId as FailureModeId);
  return `You’ve marked “${title}” as not a concern ${candidate.count} times, each time because ${dismissReasonBecause(candidate.reason)}.`;
}

export default function CheckTuning({
  candidate,
  onMute,
  onKeep,
}: CheckTuningProps) {
  return (
    <aside className="ct" aria-label="Check tuning">
      <Label mono>Across your reviews</Label>
      <p className="ct__claim">{tuningSentence(candidate)}</p>
      <p className="ct__body">
        Ceiling can stop surfacing it by default in new reviews. You can turn it
        back on any time from <em>What it checks</em>.
      </p>
      <div className="ct__row">
        <Button variant="primary" marker onClick={onMute}>
          Mute this check
        </Button>
        <Button variant="secondary" onClick={onKeep}>
          Keep showing it
        </Button>
      </div>
      {/* Muting is not deletion, and a reviewer who is about to press it should
          know that before they do rather than afterwards. */}
      <p className="ct__note">
        Muting never removes a finding. Findings from a muted check move to a
        “Muted checks” group at the bottom of the list, and the check keeps
        running.
      </p>
    </aside>
  );
}
